import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { browserAppearance } from '../appearance/appearanceController';
import { useAuth, useCurrentUser } from '../auth/AuthProvider';
import {
  fetchMyPhoto,
  fetchMyProfile,
  removeMyPhoto,
  saveMyProfile,
  saveMyTabOrder,
  saveMyWeekStart,
  uploadMyPhoto,
} from './profileApi';
import type { MyProfile, WeekStartsOn } from './profileModel';

interface ProfileContextValue {
  email: string;
  /** The saved display name once loaded; the token's name until then. */
  displayName: string;
  profile: MyProfile | null;
  loadError: string | null;
  /** An object URL for the caller's photo, or undefined when there is none. */
  avatarUrl: string | undefined;
  saveProfile(fields: { displayName: string; title: string | null; about: string | null }): Promise<void>;
  saveWeekStart(weekStartsOn: WeekStartsOn): Promise<void>;
  /** Shows the new order at once and saves it; a failed save puts the old order back. */
  saveTabOrder(app: string, tabs: readonly string[]): Promise<void>;
  uploadPhoto(file: Blob): Promise<void>;
  removePhoto(): Promise<void>;
  /** Signs out and forgets this browser's last-user theme, so the sign-in screen is not themed. */
  signOut(): Promise<void>;
}

const ProfileContext = createContext<ProfileContextValue | undefined>(undefined);

/**
 * The signed-in person's own profile and photo, shared by the user menu, the
 * photo dialog and Your settings so a change in one shows in all of them.
 * Also applies their appearance choice while they are signed in.
 */
export function ProfileProvider({ children }: { children: ReactNode }): JSX.Element {
  const user = useCurrentUser();
  const auth = useAuth();
  const [profile, setProfile] = useState<MyProfile | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [photo, setPhoto] = useState<Blob | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    browserAppearance().activate(user.email);
    return () => browserAppearance().deactivate();
  }, [user.email]);

  useEffect(() => {
    let active = true;
    fetchMyProfile()
      .then((loaded) => {
        if (!active) return;
        setProfile(loaded);
        if (loaded.hasPhoto) {
          return fetchMyPhoto().then((blob) => {
            if (active) setPhoto(blob);
          });
        }
        return undefined;
      })
      .catch((error: unknown) => {
        if (active) setLoadError(error instanceof Error ? error.message : 'Your profile could not be loaded.');
      });
    return () => {
      active = false;
    };
  }, [user.email]);

  useEffect(() => {
    if (photo === null) {
      setAvatarUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(photo);
    setAvatarUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const saveProfile = useCallback<ProfileContextValue['saveProfile']>(async (fields) => {
    setProfile(await saveMyProfile(fields));
  }, []);

  const saveWeekStart = useCallback(async (weekStartsOn: WeekStartsOn) => {
    setProfile(await saveMyWeekStart(weekStartsOn));
  }, []);

  const saveTabOrder = useCallback(async (app: string, tabs: readonly string[]) => {
    let before: readonly string[] | undefined;
    setProfile((current) => {
      if (current === null) return current;
      before = current.tabOrder[app];
      return { ...current, tabOrder: { ...current.tabOrder, [app]: tabs } };
    });
    try {
      setProfile(await saveMyTabOrder(app, tabs));
    } catch (error) {
      setProfile((current) => {
        if (current === null) return current;
        const { [app]: _dropped, ...rest } = current.tabOrder;
        return { ...current, tabOrder: before ? { ...rest, [app]: before } : rest };
      });
      throw error;
    }
  }, []);

  const uploadPhoto = useCallback(async (file: Blob) => {
    await uploadMyPhoto(file);
    setPhoto(file);
    setProfile((current) => (current === null ? current : { ...current, hasPhoto: true }));
  }, []);

  const removePhoto = useCallback(async () => {
    await removeMyPhoto();
    setPhoto(null);
    setProfile((current) => (current === null ? current : { ...current, hasPhoto: false }));
  }, []);

  const { signOut: authSignOut } = auth;
  const signOut = useCallback(async () => {
    browserAppearance().deactivate({ forget: true });
    await authSignOut();
  }, [authSignOut]);

  const value = useMemo<ProfileContextValue>(
    () => ({
      email: user.email,
      displayName: profile?.displayName ?? user.name,
      profile,
      loadError,
      avatarUrl,
      saveProfile,
      saveWeekStart,
      saveTabOrder,
      uploadPhoto,
      removePhoto,
      signOut,
    }),
    [user, profile, loadError, avatarUrl, saveProfile, saveWeekStart, saveTabOrder, uploadPhoto, removePhoto, signOut],
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

export function useMyProfile(): ProfileContextValue {
  const context = useContext(ProfileContext);
  if (context === undefined) throw new Error('useMyProfile must be used inside ProfileProvider.');
  return context;
}
